import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../../api.ts';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  PageHeader,
  Panel,
  Select,
  Table,
} from '../../components/ui.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import { fileToBase64 } from '../legal/LegalRecords.tsx';
import { useFormat } from '../purchasing/common.tsx';

const LINK = 'text-accent underline-offset-2 hover:underline';

export interface ImportKindCard {
  id: string;
  group: 'career' | 'structure' | 'finance' | 'people' | 'partners';
  order: number;
  version: number;
  dependsOn: string[];
  prerequisites: string[];
  options: { key: 'defaultValidFrom'; type: 'date' }[];
  count: number | null;
  lastImport: { id: string; status: string; at: string } | null;
  actions: { template: boolean; export: boolean; validate: boolean; apply: boolean };
}

export interface BatchIssue {
  rowNumber: number;
  field: string | null;
  code: string;
  severity: 'error' | 'warning';
  message: string;
  hint: string | null;
}

export interface BatchView {
  id: string;
  kind: string;
  fileName: string;
  fileSize: number;
  sha256: string;
  status: string;
  rowsTotal: number;
  counts: Partial<
    Record<
      'total' | 'valid' | 'invalid' | 'create' | 'update' | 'unchanged' | 'conflict' | 'reconcile',
      number
    >
  >;
  result: Partial<Record<'created' | 'updated' | 'ignored' | 'rejected', number>> | null;
  failure: string | null;
  expiresAt: string;
  createdAt: string;
  appliedAt: string | null;
  userName?: string | null;
  rows: { rowNumber: number; key: string | null; operation: string; status: string }[];
  issues: BatchIssue[];
  actions: { apply: boolean; cancel: boolean };
}

const STATUS_TONE: Record<string, 'ok' | 'off' | 'info' | 'accent' | 'neutral'> = {
  done: 'ok',
  ready: 'info',
  validated: 'neutral',
  invalid: 'off',
  failed: 'off',
  cancelled: 'neutral',
  expired: 'neutral',
  partial: 'accent',
  processing: 'info',
  received: 'neutral',
  validating: 'info',
};
const ROW_TONE: Record<string, 'ok' | 'off' | 'info' | 'accent' | 'neutral'> = {
  valid: 'ok',
  warning: 'accent',
  invalid: 'off',
  conflict: 'off',
  reconcile: 'accent',
};

export function BatchStatus({ status }: { status: string }) {
  const { t } = useTranslation();
  return <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{t(`imports.status.${status}`)}</Badge>;
}

const templateUrl = (kind: string, mode: 'empty' | 'update') =>
  `/api/masterdata-imports/kinds/${kind}/template?mode=${mode}`;

/**
 * Central de Importações de Dados Mestres (ADR 0040): um card por cadastro, na ordem das
 * dependências, com o template oficial, a importação (envio, pré-validação, prévia e confirmação)
 * e o histórico. Nada é gravado antes da confirmação expressa do mesmo arquivo validado.
 */
export function ImportsPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const [selected, setSelected] = useState<string | null>(null);
  const kinds = useQuery({
    queryKey: ['masterdata-imports', 'kinds'],
    queryFn: () =>
      api.get<{ items: ImportKindCard[]; confirmMinutes: number; maxFileBytes: number }>(
        '/masterdata-imports/kinds',
      ),
  });
  if (kinds.isPending) return <Loading />;
  if (kinds.isError) return <ErrorBox error={kinds.error} />;
  const current = kinds.data.items.find((k) => k.id === selected) ?? null;
  const groups = (['career', 'structure', 'finance', 'people', 'partners'] as const)
    .map((g) => ({ g, items: kinds.data.items.filter((k) => k.group === g) }))
    .filter((x) => x.items.length > 0);
  return (
    <>
      <PageHeader
        title={t('imports.title')}
        description={t('imports.description')}
        actions={
          <Link to="/masterdata/imports/history" className={`text-sm ${LINK}`}>
            {t('imports.historyLink')}
          </Link>
        }
      />
      <Panel title={t('imports.howTitle')}>
        <ol className="list-decimal pl-5 text-sm">
          {(['template', 'fill', 'send', 'review', 'confirm'] as const).map((s) => (
            <li key={s}>{t(`imports.how.${s}`, { minutes: kinds.data.confirmMinutes })}</li>
          ))}
        </ol>
        <p className="text-xs text-muted">
          {t('imports.orderHint')}{' '}
          {kinds.data.items
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((k) => `${k.order}. ${t(`imports.kinds.${k.id}.title`)}`)
            .join(' → ')}
        </p>
      </Panel>
      {current && (
        <ImportWizard
          kind={current}
          maxBytes={kinds.data.maxFileBytes}
          onClose={() => setSelected(null)}
        />
      )}
      {groups.map(({ g, items }) => (
        <Panel key={g} title={t(`imports.groups.${g}`)}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {items.map((k) => (
              <div
                key={k.id}
                className="flex flex-col gap-2 rounded-lg border border-rule bg-paper p-3"
                data-kind={k.id}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">
                      {k.order}. {t(`imports.kinds.${k.id}.title`)}
                    </p>
                    <p className="text-xs text-muted">{t(`imports.kinds.${k.id}.description`)}</p>
                  </div>
                  <span className="text-xs text-muted whitespace-nowrap">v{k.version}</span>
                </div>
                <dl className="grid grid-cols-2 gap-x-2 text-xs">
                  <dt className="text-muted">{t('imports.records')}</dt>
                  <dd className="tabular-nums">{k.count ?? '—'}</dd>
                  <dt className="text-muted">{t('imports.lastImport')}</dt>
                  <dd>
                    {k.lastImport ? (
                      <span className="flex flex-wrap items-center gap-1">
                        {fmt.dateTime(k.lastImport.at)} <BatchStatus status={k.lastImport.status} />
                      </span>
                    ) : (
                      t('imports.never')
                    )}
                  </dd>
                </dl>
                {(k.dependsOn.length > 0 || k.prerequisites.length > 0) && (
                  <p className="text-xs text-muted">
                    {t('imports.dependsOn')}{' '}
                    {[
                      ...k.dependsOn.map((d) => t(`imports.kinds.${d}.title`)),
                      ...k.prerequisites,
                    ].join(', ')}
                  </p>
                )}
                <div className="mt-auto flex flex-wrap gap-2">
                  <a className={`text-sm ${LINK}`} href={templateUrl(k.id, 'empty')}>
                    {t('imports.downloadTemplate')}
                  </a>
                  {k.actions.export && (
                    <a className={`text-sm ${LINK}`} href={templateUrl(k.id, 'update')}>
                      {t('imports.downloadExisting')}
                    </a>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button disabled={!k.actions.validate} onClick={() => setSelected(k.id)}>
                    {t('imports.import')}
                  </Button>
                  <a
                    href={`/masterdata/imports/history?kind=${k.id}`}
                    className={`self-center text-sm ${LINK}`}
                  >
                    {t('imports.history')}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      ))}
    </>
  );
}

/** O fluxo de um cadastro na própria Central, num painel. */
function ImportWizard(props: { kind: ImportKindCard; maxBytes: number; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Panel title={t('imports.wizardTitle', { name: t(`imports.kinds.${props.kind.id}.title`) })}>
      <ImportFlow {...props} />
    </Panel>
  );
}

/**
 * Envio → pré-validação → prévia → confirmação → resultado. É o mesmo fluxo na Central e no botão
 * Importar das telas de Dados Mestres (ADR 0040, adendo de 28/10/2026).
 */
export function ImportFlow({
  kind,
  maxBytes,
  onClose,
}: {
  kind: ImportKindCard;
  maxBytes: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [file, setFile] = useState<{ fileName: string; contentBase64: string } | null>(null);
  const [tooBig, setTooBig] = useState(false);
  const [defaultValidFrom, setDefaultValidFrom] = useState('');
  const [batch, setBatch] = useState<BatchView | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const reset = () => {
    setBatch(null);
    setConfirmed(false);
    validate.reset();
    apply.reset();
  };
  const validate = useMutation({
    mutationFn: () =>
      api.post<BatchView>(`/masterdata-imports/kinds/${kind.id}/validate`, {
        ...file!,
        options: { defaultValidFrom },
      }),
    onSuccess: async (b) => {
      setBatch(b);
      setConfirmed(false);
      await qc.invalidateQueries({ queryKey: ['masterdata-imports'] });
    },
  });
  const apply = useMutation({
    mutationFn: () =>
      api.post<BatchView>(`/masterdata-imports/batches/${batch!.id}/apply`, {
        ...file!,
        sha256: batch!.sha256,
        confirm: true,
      }),
    onSuccess: async (b) => {
      setBatch(b);
      await qc.invalidateQueries();
    },
  });
  const blocked = apply.error instanceof ApiError && apply.error.code === 'apply_blocked';
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field
          label={t('imports.file')}
          hint={t('imports.fileHint', { kb: Math.round(maxBytes / 1024) })}
        >
          <Input
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={async (ev) => {
              const chosen = ev.target.files?.[0];
              reset();
              setTooBig(!!chosen && chosen.size > maxBytes);
              setFile(chosen && chosen.size <= maxBytes ? await fileToBase64(chosen) : null);
            }}
          />
        </Field>
        {kind.options.some((o) => o.key === 'defaultValidFrom') && (
          <Field label={t('imports.defaultValidFrom')} hint={t('imports.defaultValidFromHint')}>
            <Input
              type="date"
              value={defaultValidFrom}
              onChange={(ev) => {
                setDefaultValidFrom(ev.target.value);
                reset();
              }}
            />
          </Field>
        )}
      </div>
      {tooBig && (
        <p className="text-sm text-danger">
          {t('imports.tooBig', { kb: Math.round(maxBytes / 1024) })}
        </p>
      )}
      <ErrorBox error={validate.error} />
      <div className="flex gap-2">
        <Button disabled={!file || validate.isPending} onClick={() => validate.mutate()}>
          {validate.isPending ? t('imports.validating') : t('imports.validate')}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </div>
      {batch && <BatchDetail batch={batch} />}
      {batch?.actions.apply && (
        <div className="flex flex-col gap-2 rounded-md border border-rule bg-paper p-3">
          <p className="text-sm font-medium">{t('imports.confirmTitle')}</p>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(ev) => setConfirmed(ev.target.checked)}
            />
            {t('imports.confirmText', {
              create: batch.counts.create ?? 0,
              update: batch.counts.update ?? 0,
              unchanged: batch.counts.unchanged ?? 0,
            })}
          </label>
          {blocked ? (
            <div role="alert" className="text-sm text-danger">
              <p>{t('imports.applyBlocked')}</p>
              <ul className="list-disc pl-5 text-xs">
                {(apply.error as ApiError).issues.slice(0, 20).map((i, idx) => (
                  <li key={idx}>{i.message}</li>
                ))}
              </ul>
            </div>
          ) : (
            <ErrorBox error={apply.error} />
          )}
          <div>
            <Button disabled={!confirmed || apply.isPending} onClick={() => apply.mutate()}>
              {apply.isPending ? t('imports.applying') : t('imports.apply')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Resumo, prévia linha a linha (com filtro dos problemas), inconsistências e download do relatório. */
export function BatchDetail({ batch }: { batch: BatchView }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const [onlyProblems, setOnlyProblems] = useState(false);
  const issuesByRow = useMemo(() => {
    const m = new Map<number, BatchIssue[]>();
    for (const i of batch.issues) m.set(i.rowNumber, [...(m.get(i.rowNumber) ?? []), i]);
    return m;
  }, [batch.issues]);
  const fileIssues = issuesByRow.get(0) ?? [];
  const rows = batch.rows.filter((r) => !onlyProblems || r.status !== 'valid');
  const counts = batch.counts;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <BatchStatus status={batch.status} />
        <span className="font-medium">{batch.fileName}</span>
        <span className="text-xs text-muted">
          {fmt.bytes(batch.fileSize)} · {fmt.dateTime(batch.createdAt)} ·{' '}
          <span className="font-mono" title={batch.sha256}>
            {batch.sha256.slice(0, 12)}…
          </span>
        </span>
      </div>
      {batch.status === 'ready' && (
        <p className="text-xs text-muted">
          {t('imports.expiresAt', { at: fmt.dateTime(batch.expiresAt) })}
        </p>
      )}
      {batch.failure && <p className="text-sm text-danger">{batch.failure}</p>}
      <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4 lg:grid-cols-8">
        {(
          [
            'total',
            'valid',
            'invalid',
            'create',
            'update',
            'unchanged',
            'conflict',
            'reconcile',
          ] as const
        ).map((k) => (
          <div key={k} className="rounded-md border border-rule p-2" data-count={k}>
            <dt className="text-xs text-muted">{t(`imports.counts.${k}`)}</dt>
            <dd className="text-lg tabular-nums">{counts[k] ?? 0}</dd>
          </div>
        ))}
      </dl>
      {batch.result && (
        <p className="text-sm">
          {t('imports.result', {
            created: batch.result.created ?? 0,
            updated: batch.result.updated ?? 0,
            ignored: batch.result.ignored ?? 0,
            rejected: batch.result.rejected ?? 0,
          })}
        </p>
      )}
      {fileIssues.length > 0 && (
        <div
          role="alert"
          className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
        >
          <p className="font-medium">{t('imports.fileProblems')}</p>
          <ul className="list-disc pl-5">
            {fileIssues.map((i, idx) => (
              <li key={idx}>
                {i.message}
                {i.hint && <span className="block text-xs opacity-80">{i.hint}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {batch.issues.length > 0 && (
        <a
          className={`text-sm ${LINK}`}
          href={`/api/masterdata-imports/batches/${batch.id}/issues`}
        >
          {t('imports.downloadIssues', { n: batch.issues.length })}
        </a>
      )}
      {batch.rows.length > 0 && (
        <>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={onlyProblems}
              onChange={(ev) => setOnlyProblems(ev.target.checked)}
            />
            {t('imports.onlyProblems')}
          </label>
          <Table
            head={[
              t('imports.row'),
              t('imports.key'),
              t('imports.operationLabel'),
              t('imports.situation'),
              t('imports.messages'),
            ]}
          >
            {rows.length === 0 && <EmptyRow colSpan={5}>{t('imports.noRows')}</EmptyRow>}
            {rows.slice(0, 1000).map((r) => (
              <tr key={r.rowNumber}>
                <td className="tabular-nums">{r.rowNumber}</td>
                <td className="font-mono text-xs">{r.key ?? '—'}</td>
                <td>{t(`imports.operation.${r.operation}`)}</td>
                <td>
                  <Badge tone={ROW_TONE[r.status] ?? 'neutral'}>
                    {t(`imports.rowStatus.${r.status}`)}
                  </Badge>
                </td>
                <td className="text-xs">
                  {(issuesByRow.get(r.rowNumber) ?? []).map((i, idx) => (
                    <p key={idx} className={i.severity === 'error' ? 'text-danger' : 'text-muted'}>
                      {i.message}
                    </p>
                  ))}
                </td>
              </tr>
            ))}
          </Table>
          {rows.length > 1000 && (
            <p className="text-xs text-muted">{t('imports.moreRows', { n: rows.length - 1000 })}</p>
          )}
        </>
      )}
    </div>
  );
}

/** Histórico das importações que a pessoa pode ver (as próprias; da empresa ou do grupo, com a permissão). */
export function ImportHistoryPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const initial =
    new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search).get('kind') ??
    '';
  const [kind, setKind] = useState(initial);
  const kinds = useQuery({
    queryKey: ['masterdata-imports', 'kinds'],
    queryFn: () => api.get<{ items: ImportKindCard[] }>('/masterdata-imports/kinds'),
  });
  const history = useQuery({
    queryKey: ['masterdata-imports', 'history', kind],
    queryFn: () =>
      api.get<(Omit<BatchView, 'rows' | 'issues' | 'actions'> & { userName: string | null })[]>(
        `/masterdata-imports/batches?limit=200${kind ? `&kind=${kind}` : ''}`,
      ),
  });
  return (
    <>
      <PageHeader
        title={t('imports.historyTitle')}
        description={t('imports.historyDescription')}
        actions={
          <Link to="/masterdata/imports" className={`text-sm ${LINK}`}>
            {t('imports.backToCenter')}
          </Link>
        }
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label={t('imports.kind')}>
          <Select value={kind} onChange={(ev) => setKind(ev.target.value)}>
            <option value="">{t('common.all')}</option>
            {kinds.data?.items.map((k) => (
              <option key={k.id} value={k.id}>
                {t(`imports.kinds.${k.id}.title`)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {history.isPending ? (
        <Loading />
      ) : history.isError ? (
        <ErrorBox error={history.error} />
      ) : (
        <Table
          head={[
            t('imports.batch'),
            t('imports.kind'),
            t('imports.when'),
            t('imports.file'),
            t('imports.counts.total'),
            t('imports.errors'),
            t('imports.situation'),
            t('imports.resultLabel'),
            '',
          ]}
        >
          {history.data.length === 0 && <EmptyRow colSpan={9}>{t('imports.noHistory')}</EmptyRow>}
          {history.data.map((b) => (
            <tr key={b.id}>
              <td>
                <Link
                  to="/masterdata/imports/batches/$id"
                  params={{ id: b.id }}
                  className={`font-mono text-xs ${LINK}`}
                >
                  {b.id.slice(-8)}
                </Link>
              </td>
              <td>{t(`imports.kinds.${b.kind}.title`, { defaultValue: b.kind })}</td>
              <td className="text-xs">
                <span className="whitespace-nowrap">{fmt.dateTime(b.createdAt)}</span>
                {b.userName && (
                  <div className="text-muted">
                    {t('imports.by')}: {b.userName}
                  </div>
                )}
              </td>
              <td className="min-w-48 text-xs break-all">
                {b.fileName}
                <div className="text-muted">{t('imports.notStored')}</div>
              </td>
              <td className="tabular-nums">{b.rowsTotal}</td>
              <td className="tabular-nums">{b.counts.invalid ?? 0}</td>
              <td>
                <BatchStatus status={b.status} />
              </td>
              <td className="text-xs">
                {b.result
                  ? t('imports.resultShort', {
                      created: b.result.created ?? 0,
                      updated: b.result.updated ?? 0,
                      ignored: b.result.ignored ?? 0,
                    })
                  : '—'}
              </td>
              <td className="text-right">
                <MasterDeleteButton table="mdimport.batch" id={b.id} compact />
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}

/** Detalhe de um lote (prévia, inconsistências e, para quem enviou, cancelar). */
export function ImportBatchPage() {
  const { t } = useTranslation();
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const batch = useQuery({
    queryKey: ['masterdata-imports', 'batch', id],
    queryFn: () => api.get<BatchView>(`/masterdata-imports/batches/${id}`),
  });
  const cancel = useMutation({
    mutationFn: () => api.post<BatchView>(`/masterdata-imports/batches/${id}/cancel`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['masterdata-imports'] }),
  });
  return (
    <>
      <PageHeader
        title={t('imports.batchTitle', { id: id.slice(-8) })}
        description={batch.data ? t(`imports.kinds.${batch.data.kind}.title`) : undefined}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/masterdata/imports/history" className={`text-sm ${LINK}`}>
              {t('imports.historyLink')}
            </Link>
            <MasterDeleteButton
              table="mdimport.batch"
              id={id}
              onDeleted={() => void navigate({ to: '/masterdata/imports/history' })}
            />
          </div>
        }
      />
      {batch.isPending ? (
        <Loading />
      ) : batch.isError ? (
        <ErrorBox error={batch.error} />
      ) : (
        <Panel>
          <BatchDetail batch={batch.data} />
          {batch.data.actions.apply && (
            <p className="text-sm text-muted">{t('imports.applyFromCenter')}</p>
          )}
          <ErrorBox error={cancel.error} />
          {batch.data.actions.cancel && (
            <div>
              <Button
                variant="secondary"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate()}
              >
                {t('imports.cancel')}
              </Button>
            </div>
          )}
        </Panel>
      )}
    </>
  );
}
